import React, { useState, useEffect, useCallback } from 'react';
import ComplianceHarnessPanel, { FIELD } from './ComplianceHarnessPanel';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HarnessPanel — Live Simulation Harness UI (owner-only).
 *
 * Purpose: expose the two silent-failing paths the 2026-02
 * Message-803 hotfix restored so we can trigger them against
 * test data and verify the resulting `maintenance_action_log`
 * rows actually land.
 *
 * Structural safety lives in the backend (see routes/harness.py):
 * this panel only surfaces harness-client data because the
 * endpoints filter rows to `is_harness_client=True` agents and
 * refuse triggers on non-harness MRs. If the panel accidentally
 * shows a real client's row, the backend gate has failed and
 * the CI invariant `test_harness_verify_gate_ci.py` should have
 * caught it in the last deploy.
 */
export default function HarnessPanel({ adminKey }) {
  const [clients, setClients] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [rows, setRows] = useState([]);
  const [errorCount, setErrorCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mrRefFilter, setMrRefFilter] = useState('');
  const [decisionFilter, setDecisionFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [hoursFilter, setHoursFilter] = useState(24);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [triggerMrRef, setTriggerMrRef] = useState('');
  const [triggerResult, setTriggerResult] = useState(null);
  const [triggering, setTriggering] = useState('');
  const [markClientId, setMarkClientId] = useState('');
  const [markCompanyName, setMarkCompanyName] = useState('');

  const headers = { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' };

  const loadClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/harness/clients`, { headers });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
      const cres = await fetch(`${API_BASE_URL}/api/admin/harness/candidates`, { headers });
      if (cres.ok) setCandidates((await cres.json()).clients || []);
    } catch (err) {
      setError('Failed to load harness clients: ' + err.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (mrRefFilter) params.append('mr_ref', mrRefFilter);
      if (decisionFilter) params.append('decision', decisionFilter);
      if (siteFilter) params.append('site', siteFilter);
      params.append('hours', String(hoursFilter));
      const res = await fetch(
        `${API_BASE_URL}/api/admin/harness/action-log?${params}`,
        { headers },
      );
      if (!res.ok) {
        const text = await res.text();
        setError(`Load failed (${res.status}): ${text}`);
        return;
      }
      const data = await res.json();
      setRows(data.rows || []);
      setErrorCount(data.error_count || 0);
    } catch (err) {
      setError('Load failed: ' + err.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminKey, mrRefFilter, decisionFilter, siteFilter, hoursFilter]);

  useEffect(() => {
    loadClients();
    loadRows();
  }, [loadClients, loadRows]);

  const trigger = async (scenario) => {
    if (!triggerMrRef.trim()) {
      setError('Enter an MR ref before firing a scenario.');
      return;
    }
    setTriggering(scenario);
    setTriggerResult(null);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/harness/trigger/${scenario}`,
        { method: 'POST', headers, body: JSON.stringify({ mr_ref: triggerMrRef.trim() }) },
      );
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { raw: text }; }
      if (!res.ok) {
        setError(`Trigger refused (${res.status}): ${data.detail || text}`);
        return;
      }
      setTriggerResult(data);
      // Refresh log after a short delay so the row lands.
      setTimeout(loadRows, 500);
    } catch (err) {
      setError('Trigger failed: ' + err.message);
    } finally {
      setTriggering('');
    }
  };

  const setRedirect = async (c, enabled) => {
    const typed = window.prompt(
      `${enabled ? 'Redirect ALL of this account\u2019s emails to the harness inbox' : 'Send this account\u2019s emails to the REAL addresses on its records'}.\nType the company name to confirm:`);
    if (typed === null) return;
    const res = await fetch(`${API_BASE_URL}/api/admin/harness/redirect/${c.id}`, {
      method: 'POST', headers, body: JSON.stringify({ enabled, confirm_company_name: typed }) });
    const data = await res.json();
    if (!res.ok) { setError(`Redirect change failed: ${data.detail || res.status}`); return; }
    setError('');
    loadClients();
  };

  const markAsHarness = async (isFlag) => {
    if (!markClientId.trim()) {
      setError('Choose an account before marking.');
      return;
    }
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/harness/mark-client/${markClientId.trim()}`,
        {
          method: 'POST', headers,
          body: JSON.stringify({ is_harness_client: !!isFlag, confirm_company_name: markCompanyName }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setError(`Mark failed: ${data.detail || res.status}`);
        return;
      }
      loadClients();
      setMarkClientId('');
      setMarkCompanyName('');
    } catch (err) {
      setError('Mark failed: ' + err.message);
    }
  };

  return (
    <div className="admin-harness" data-testid="admin-harness-panel" style={{ padding: '1.5rem' }}>
      <h2 style={{ marginTop: 0 }}>Live Simulation Harness</h2>
      <p style={{ color: '#666', fontSize: '.9em' }}>
        Owner-only. Harness buttons work only on accounts marked below. Their emails go to the
        REAL addresses on the account's records unless the email redirect is turned on for that
        account, in which case everything goes to <code>MAINT_HARNESS_TO</code>.
      </p>

      {error && (
        <div
          data-testid="harness-error"
          style={{ background: '#fee', border: '1px solid #c00', color: '#900', padding: '.75rem', margin: '1rem 0', borderRadius: 4 }}
        >
          {error}
        </div>
      )}

      {/* Section 1 — harness clients */}
      <section style={{ marginTop: '1.5rem' }}>
        <h3>Harness Clients</h3>
        <div style={{ marginBottom: '.75rem' }}>
          {clients.length === 0 ? (
            <em>No agents flagged as harness clients yet. Mark one below.</em>
          ) : (
            <ul data-testid="harness-clients-list">
              {clients.map(c => (
                <li key={c.id} style={{ fontSize: '.9em', marginBottom: '.35rem' }} data-testid={`harness-client-${c.id}`}>
                  <b>{c.company_name}</b> ({c.contact_email || '—'}) —{' '}
                  <span data-testid={`harness-redirect-state-${c.id}`}
                    style={{ fontWeight: 700, color: c.redirect_emails ? '#b45309' : '#15803d' }}>
                    {c.redirect_emails ? 'Emails: redirected to harness inbox' : 'Emails: real addresses'}
                  </span>{' '}
                  <button type="button" data-testid={`harness-redirect-toggle-${c.id}`}
                    onClick={() => setRedirect(c, !c.redirect_emails)}
                    style={{ marginLeft: '.4rem', padding: '2px 8px', border: '1px solid #94a3b8', borderRadius: 4, background: '#fff', color: '#0f172a', cursor: 'pointer' }}>
                    {c.redirect_emails ? 'Turn redirect off' : 'Turn redirect on'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={markClientId}
            onChange={e => setMarkClientId(e.target.value)}
            data-testid="harness-mark-client-select"
            style={{ ...FIELD, minWidth: 320 }}
          >
            <option value="">Choose an account…</option>
            {candidates.map(c => (
              <option key={c.id} value={c.id}>
                {c.company_name || '(no company name)'} — {c.contact_email || 'no email'}{c.is_harness_client ? ' [harness]' : ''}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="type the client's company name to confirm"
            value={markCompanyName}
            onChange={e => setMarkCompanyName(e.target.value)}
            data-testid="harness-mark-client-company-confirm"
            style={{ ...FIELD, minWidth: 300 }}
          />
          <button onClick={() => markAsHarness(true)} data-testid="harness-mark-client-on">
            Mark as harness
          </button>
          <button onClick={() => markAsHarness(false)} data-testid="harness-mark-client-off">
            Unmark
          </button>
        </div>
      </section>

      <ComplianceHarnessPanel adminKey={adminKey} harnessClients={clients} />

      {/* Section 2 — trigger scenarios */}
      <section style={{ marginTop: '2rem' }}>
        <h3>Trigger Scenarios</h3>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.75rem' }}>
          <input
            type="text"
            placeholder="MR ref (e.g. MR-V2-XXXX)"
            value={triggerMrRef}
            onChange={e => setTriggerMrRef(e.target.value)}
            data-testid="harness-trigger-mr-ref"
            style={{ fontFamily: 'monospace', minWidth: 240 }}
          />
          <button
            onClick={() => trigger('prebooking_dead_end')}
            disabled={!!triggering}
            data-testid="harness-trigger-prebooking-dead-end"
          >
            {triggering === 'prebooking_dead_end' ? 'Firing…' : 'Fire pre-booking dead-end'}
          </button>
          <button
            onClick={() => trigger('past_time_book')}
            disabled={!!triggering}
            data-testid="harness-trigger-past-time-book"
          >
            {triggering === 'past_time_book' ? 'Firing…' : 'Fire past-time book'}
          </button>
        </div>
        {triggerResult && (
          <pre
            data-testid="harness-trigger-result"
            style={{ background: '#f5f5f5', padding: '.75rem', fontSize: '.85em', borderRadius: 4 }}
          >
            {JSON.stringify(triggerResult, null, 2)}
          </pre>
        )}
      </section>

      {/* Section 3 — action-log view */}
      <section style={{ marginTop: '2rem' }}>
        <h3>
          maintenance_action_log
          {errorCount > 0 && (
            <span
              data-testid="harness-error-count-badge"
              style={{ marginLeft: 8, background: '#c00', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: '.75em', fontWeight: 700 }}
            >
              {errorCount} error{errorCount === 1 ? '' : 's'}
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', marginBottom: '.75rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="mr_ref filter"
            value={mrRefFilter}
            onChange={e => setMrRefFilter(e.target.value)}
            data-testid="harness-filter-mr-ref"
            style={{ fontFamily: 'monospace' }}
          />
          <select
            value={decisionFilter}
            onChange={e => setDecisionFilter(e.target.value)}
            data-testid="harness-filter-decision"
          >
            <option value="">any decision</option>
            <option value="error">error</option>
            <option value="accept">accept</option>
            <option value="reject">reject</option>
            <option value="would_alert_if_promoted">would_alert_if_promoted</option>
            <option value="ok">ok</option>
          </select>
          <input
            type="text"
            placeholder="site filter (exact)"
            value={siteFilter}
            onChange={e => setSiteFilter(e.target.value)}
            data-testid="harness-filter-site"
            style={{ fontFamily: 'monospace' }}
          />
          <select
            value={hoursFilter}
            onChange={e => setHoursFilter(parseInt(e.target.value, 10))}
            data-testid="harness-filter-hours"
          >
            <option value={1}>last 1h</option>
            <option value={6}>last 6h</option>
            <option value={24}>last 24h</option>
            <option value={72}>last 72h</option>
            <option value={168}>last 7d</option>
          </select>
          <button onClick={loadRows} disabled={loading} data-testid="harness-refresh">
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {rows.length === 0 ? (
          <p data-testid="harness-no-rows"><em>No rows in the selected window.</em></p>
        ) : (
          <table
            className="admin-table"
            data-testid="harness-rows-table"
            style={{ width: '100%', fontSize: '.85em' }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: 180 }}>ts</th>
                <th style={{ textAlign: 'left' }}>site</th>
                <th style={{ textAlign: 'left' }}>decision</th>
                <th style={{ textAlign: 'left' }}>mr_ref</th>
                <th style={{ textAlign: 'left' }}>reason</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const isErr = r.decision === 'error';
                const isOpen = expandedRowId === r.id;
                return (
                  <React.Fragment key={r.id}>
                    <tr
                      data-testid={`harness-row-${r.id}`}
                      style={{ background: isErr ? '#fee' : undefined, color: isErr ? '#900' : undefined }}
                    >
                      <td style={{ fontFamily: 'monospace' }}>{r.ts || '—'}</td>
                      <td style={{ fontFamily: 'monospace' }}>{r.site || '—'}</td>
                      <td>
                        <span style={{
                          background: isErr ? '#c00' : '#eee',
                          color: isErr ? '#fff' : '#333',
                          padding: '1px 6px', borderRadius: 3, fontSize: '.85em', fontWeight: isErr ? 700 : 400,
                        }}>
                          {r.decision || '—'}
                        </span>
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>{r.meta?.mr_ref || '—'}</td>
                      <td>{r.reason || '—'}</td>
                      <td>
                        <button
                          onClick={() => setExpandedRowId(isOpen ? null : r.id)}
                          data-testid={`harness-row-expand-${r.id}`}
                        >
                          {isOpen ? 'Hide' : 'Show meta'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`harness-row-meta-${r.id}`}>
                        <td colSpan={6}>
                          <pre style={{ background: '#fafafa', padding: '.5rem', fontSize: '.8em', margin: 0 }}>
                            {JSON.stringify(r.meta || {}, null, 2)}
                          </pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
